import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getProductInfo } from '../../../Utility/Api';
import { toast } from 'react-toastify';
import { WarehouseContext } from '../../../Utility/UserContext';

const ProductPerformance = () => {
    const location = useLocation();
    const { state } = location;
    const { showLoader, hideLoader } = useContext(WarehouseContext);
    const [productData, setProductData] = useState(null);

    useEffect(() => {
        if (state?.product_id) {
            fetchPerformanceData(state?.product_id);
        }
    }, [state]);

    const fetchPerformanceData = async (productId) => {
        showLoader();
        try {
            const response = await getProductInfo(productId);
            if (response.data?.old_response) {
                setProductData(response.data?.old_response);
            } else {
                toast.error('No data found.');
            }
        } catch (error) {
            console.error('Error fetching:', error);
            toast.error('Error fetching data');
        } finally {
            hideLoader();
        }
    };

    const formatCurrency = (amount) => {
        return amount;
        // return new Intl.NumberFormat('en-US', {
        //     style: 'currency',
        //     currency: 'USD'
        // }).format(amount || 0);
    };

    const CircularProgress = ({ percentage, color = '#1890ff' }) => {
        const radius = 40;
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference - (percentage / 100) * circumference;

        return (
            <div style={{ position: 'relative', width: '100px', height: '100px' }}>
                <svg width="100" height="100" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r={radius} fill="none" stroke="#f0f0f0" strokeWidth="10" />
                    <circle
                        cx="50" cy="50" r={radius} fill="none"
                        stroke={color} strokeWidth="10"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        transform="rotate(-90 50 50)"
                    />
                </svg>
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{percentage}%</div>
                </div>
            </div>
        );
    };

    if (!productData) {
        return (
            <div className="page-wrappers">
                <div className="page-content">
                    <div className="mb-0 d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 text-uppercase">Loading product data...</h6>
                    </div>
                    <hr className='mt-1' />
                </div>
            </div>
        );
    }

    const {
        product_name,
        product_code,
        category_name,
        subcategory_name,
        total_serial_count,
        reorder_point,
        box_qty,
        barcode,
        box_bar_code,
    } = state;

    const {
        returndata,
        instocks,
        sales,
        purchase
    } = productData;

    // Calculate totals for each type
    const inventoryStock = instocks?.type_0 || { count: 0, total_purchase_price: 0, total_selling_price: 0 };
    const purchasedStock = instocks?.type_1 || { count: 0, total_purchase_price: 0, total_selling_price: 0 };
    
    const inventorySales = sales?.type_0 || { count: 0, total_purchase_price: 0, total_selling_price: 0 };
    const purchasedSales = sales?.type_1 || { count: 0, total_purchase_price: 0, total_selling_price: 0 };
    
    const inventoryPurchase = purchase?.type_0 || { count: 0, total_purchase_price: 0, total_selling_price: 0 };
    const purchasedPurchase = purchase?.type_1 || { count: 0, total_purchase_price: 0, total_selling_price: 0 };
    
    const inventoryReturns = returndata?.type_0 || { count: 0, total_purchase_price: 0, total_selling_price: 0 };
    const purchasedReturns = returndata?.type_1 || { count: 0, total_purchase_price: 0, total_selling_price: 0 };

    // Calculate totals
    const totalAvailableStock = inventoryStock.count + purchasedStock.count;
    const totalSoldStock = inventorySales.count + purchasedSales.count;
    const totalPurchasedStock = inventoryPurchase.count + purchasedPurchase.count;
    const totalReturnedStock = inventoryReturns.count + purchasedReturns.count;
    
    const totalPurchaseValue = inventoryPurchase.total_purchase_price + purchasedPurchase.total_purchase_price;
    const totalSalesValue = inventorySales.total_selling_price + purchasedSales.total_selling_price;
    const totalProfit = totalSalesValue - (inventorySales.total_purchase_price + purchasedSales.total_purchase_price);
    
    // Performance Metrics
    const inventoryPercentage = Math.min((totalAvailableStock / (Number(reorder_point || 1)) * 100), 100).toFixed(2);
    const stockTurnover = totalPurchasedStock > 0 ? ((totalSoldStock / totalPurchasedStock) * 100).toFixed(2) : 0;
    const profitMargin = totalSalesValue > 0 ? ((totalProfit / totalSalesValue) * 100).toFixed(2) : 0;

    return (
        <div className="page-wrappers">
            <div className="page-content">
                <div className="mb-0 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 text-uppercase">Product Performance: {product_name}</h6>
                </div>
                <hr className='mt-1' />

                <div className="card">
                    <div className="card-body">
                        {/* Product Info */}
                        <div className="row mb-4">
                            <div className="col-md-3 mb-3">
                                <h6 className="text-muted">Product Code</h6>
                                <p className="mb-0">{product_code}</p>
                            </div>
                            <div className="col-md-3 mb-3">
                                <h6 className="text-muted">Category</h6>
                                <p className="mb-0">{category_name} / {subcategory_name}</p>
                            </div>
                            <div className="col-md-3 mb-3">
                                <h6 className="text-muted">Barcode</h6>
                                <p className="mb-0">{barcode}</p>
                            </div>
                            <div className="col-md-3 mb-3">
                                <h6 className="text-muted">Reorder Point</h6>
                                <p className="mb-0">{reorder_point}</p>
                            </div>
                        </div>

                        {/* Stock Overview */}
                        <div className="card mb-4">
                            <div className="card-header bg-light">
                                <h5 className="mb-0">Stock Overview</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-4 text-center border-end">
                                        <h6 className="text-muted">Inventory Stock</h6>
                                        <h3 className="text-primary">{inventoryStock.count}</h3>
                                        <p className="text-muted mb-0">Value: {formatCurrency(inventoryStock.total_purchase_price)}</p>
                                    </div>
                                    <div className="col-md-4 text-center border-end">
                                        <h6 className="text-muted">Purchased Stock</h6>
                                        <h3 className="text-success">{purchasedStock.count}</h3>
                                        <p className="text-muted mb-0">Value: {formatCurrency(purchasedStock.total_purchase_price)}</p>
                                    </div>
                                    <div className="col-md-4 text-center">
                                        <h6 className="text-muted">Total Stock</h6>
                                        <h3 className="text-info">{totalAvailableStock}</h3>
                                        <p className="text-muted mb-0">Value: {formatCurrency(inventoryStock.total_purchase_price + purchasedStock.total_purchase_price)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Performance Dashboard */}
                        <div className="row">
                            {/* Stock Availability */}
                            <div className="col-md-4 mb-4">
                                <div className="card h-100">
                                    <div className="card-body text-center">
                                        <h5 className="card-title">Stock Availability</h5>
                                        <div className="d-flex justify-content-center my-3">
                                            <CircularProgress
                                                percentage={inventoryPercentage}
                                                color={totalAvailableStock <= reorder_point ? '#f5222d' : '#52c41a'}
                                            />
                                        </div>
                                        <div className="row">
                                            <div className="col-6">
                                                <h6 className="text-muted">In Stock</h6>
                                                <h4>{totalAvailableStock}</h4>
                                            </div>
                                            <div className="col-6">
                                                <h6 className="text-muted">Reorder At</h6>
                                                <h4>{reorder_point}</h4>
                                            </div>
                                        </div>
                                        {totalAvailableStock <= reorder_point && (
                                            <p className="text-danger mb-0 mt-2">
                                                <i className="fa fa-exclamation-triangle me-2"></i>
                                                Low stock alert
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Sales Performance */}
                            <div className="col-md-4 mb-4">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h5 className="card-title">Sales Performance</h5>
                                        <div className="row mt-3">
                                            <div className="col-6 mb-3">
                                                <h6 className="text-muted">Units Sold</h6>
                                                <h4>{totalSoldStock}</h4>
                                                <small className="text-muted">
                                                    (I: {inventorySales.count}, P: {purchasedSales.count})
                                                </small>
                                            </div>
                                            <div className="col-6 mb-3">
                                                <h6 className="text-muted">Turnover %</h6>
                                                <h4>{stockTurnover}%</h4>
                                            </div>
                                            <div className="col-6 mb-3">
                                                <h6 className="text-muted">Revenue</h6>
                                                <h4>{formatCurrency(totalSalesValue)}</h4>
                                            </div>
                                            <div className="col-6 mb-3">
                                                <h6 className="text-muted">Profit</h6>
                                                <h4 className="text-success">{formatCurrency(totalProfit)}</h4>
                                                <small className="text-muted">Margin: {profitMargin}%</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Inventory Movement */}
                            <div className="col-md-4 mb-4">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h5 className="card-title">Inventory Movement</h5>
                                        <div className="row mt-3">
                                            <div className="col-6 mb-3">
                                                <h6 className="text-muted">Total Stocks</h6>
                                                <h4>{totalPurchasedStock}</h4>
                                                <small className="text-muted">
                                                    (I: {inventoryPurchase.count}, P: {purchasedPurchase.count})
                                                </small>
                                            </div>
                                            <div className="col-6 mb-3">
                                                <h6 className="text-muted">Totla Value</h6>
                                                <h4>{formatCurrency(totalPurchaseValue)}</h4>
                                            </div>
                                            <div className="col-6 mb-3">
                                                <h6 className="text-muted">Returned</h6>
                                                <h4>{totalReturnedStock}</h4>
                                                <small className="text-muted">
                                                    (I: {inventoryReturns.count}, P: {purchasedReturns.count})
                                                </small>
                                            </div>
                                            <div className="col-6 mb-3">
                                                <h6 className="text-muted">Return Value</h6>
                                                <h4>{formatCurrency(inventoryReturns.total_purchase_price + purchasedReturns.total_purchase_price)}</h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Detailed Breakdown */}
                        <div className="card mt-4">
                            <div className="card-header bg-light">
                                <h5 className="mb-0">Detailed Stock Breakdown</h5>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-bordered table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Stock Type</th>
                                                <th>Available</th>
                                                <th>Purchased</th>
                                                <th>Sold</th>
                                                <th>Returned</th>
                                                <th>Purchase Value</th>
                                                <th>Sales Value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td><strong>Inventory</strong></td>
                                                <td>{inventoryStock.count}</td>
                                                <td>{inventoryPurchase.count}</td>
                                                <td>{inventorySales.count}</td>
                                                <td>{inventoryReturns.count}</td>
                                                <td>{formatCurrency(inventoryStock.total_purchase_price)}</td>
                                                <td>{formatCurrency(inventorySales.total_selling_price)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Purchased</strong></td>
                                                <td>{purchasedStock.count}</td>
                                                <td>{purchasedPurchase.count}</td>
                                                <td>{purchasedSales.count}</td>
                                                <td>{purchasedReturns.count}</td>
                                                <td>{formatCurrency(purchasedStock.total_purchase_price)}</td>
                                                <td>{formatCurrency(purchasedSales.total_selling_price)}</td>
                                            </tr>
                                            <tr className="table-active">
                                                <td><strong>Total</strong></td>
                                                <td><strong>{totalAvailableStock}</strong></td>
                                                <td><strong>{totalPurchasedStock}</strong></td>
                                                <td><strong>{totalSoldStock}</strong></td>
                                                <td><strong>{totalReturnedStock}</strong></td>
                                                <td><strong>{formatCurrency(totalPurchaseValue)}</strong></td>
                                                <td><strong>{formatCurrency(totalSalesValue)}</strong></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductPerformance;